import HighchartsReact from 'highcharts-react-official';
import * as React from 'react';
import Highcharts from 'highcharts';
import HCMore from 'highcharts/highcharts-more';
import dayjs from 'dayjs';
import constants from '@/constants';
import { Button } from 'antd';

HCMore(Highcharts);

export interface IViewChartProps {
  series?: Highcharts.SeriesOptionsType[];
  times?: string[];
  title?: string;
  pointIndex?: number;
  scrollablePlotArea?: Highcharts.ChartScrollablePlotAreaOptions;
  loadmore?: () => void;
  isFetching?: boolean;
}

const initFormat = constants.DATE_TIME_FORMAT;

const maxColumnsOnScreen = 1;
const columnWidth = 60; // Đặt chiều rộng của mỗi cột

const ViewChart: React.FC<IViewChartProps> = ({
  series = [],
  times = [],
  title = '',
  pointIndex,
  scrollablePlotArea,
  isFetching,
  loadmore,
  ...props
}) => {
  const [format, setFormat] = React.useState(initFormat);
  const chartRef = React.useRef<any>(null);
  const [zoom, setZoom] = React.useState<boolean>(false);

  const handleMouseDown = React.useCallback(
    function (event: MouseEvent) {
      const container: any = document?.querySelector('.highcharts-scrolling-parent');
      if (!container.classList.contains('isZoom')) {
        const startClientX = event.clientX;
        const highchartsScroll = container.querySelector('.highcharts-scrolling');
        const startScrollLeft = highchartsScroll.scrollLeft;

        const handleMouseMove = (event: MouseEvent) => {
          const diff = startClientX - event.clientX;
          if (highchartsScroll) {
            highchartsScroll.scrollLeft = startScrollLeft + diff;
            if (diff < 0 && highchartsScroll.scrollLeft < 5 && loadmore) {
              loadmore();
              removeEvent();
            }
          }
        };

        const removeEvent = () => {
          container?.removeEventListener('mousemove', handleMouseMove);
          container?.removeEventListener('mouseup', removeEvent);
          container?.removeEventListener('mouseleave', removeEvent);
        };

        container?.addEventListener('mouseup', removeEvent);
        container?.addEventListener('mouseleave', removeEvent);
        container?.addEventListener('mousemove', handleMouseMove);
      }
    },
    [loadmore],
  );

  React.useEffect(() => {
    if (zoom) {
      if (chartRef?.current?.chart?.zooming) chartRef.current.chart.zooming.type = 'x';
      document?.querySelector('.highcharts-scrolling-parent')?.classList.add('isZoom');
    } else {
      if (chartRef?.current?.chart?.zooming) chartRef.current.chart.zooming.type = undefined;
      chartRef?.current?.chart?.zoomOut();
      document?.querySelector('.highcharts-scrolling-parent')?.classList.remove('isZoom');
    }
  }, [zoom]);

  React.useEffect(() => {
    const container = document.querySelector('.highcharts-scrolling-parent');
    if (!container?.classList?.contains('scrollChart')) {
      container?.classList.add('scrollChart');
      document.addEventListener('mousedown', function (event) {
        const t: any = event.target;
        const targetElement = t.closest('.highcharts-scrolling-parent');
        if (targetElement) {
          handleMouseDown(event);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const highchartsScroll = chartRef.current?.container?.current?.querySelector('.highcharts-scrolling');
    if (isFetching === false && highchartsScroll) {
      highchartsScroll.scrollLeft = highchartsScroll.clientWidth;
    }
  }, [isFetching]);

  const cTimes = times;
  const cSeries = series;

  React.useEffect(() => {
    if (chartRef.current) {
      chartRef.current.chart.update({
        scrollablePlotArea: {
          minWidth: Math.max(cTimes.length * columnWidth, maxColumnsOnScreen * columnWidth),
        },
      });
    }
  }, [cTimes.length, cSeries.length]);

  const options: Highcharts.Options = React.useMemo(
    () => ({
      accessibility: { enabled: true },
      chart: {
        height: 300,

        zooming: zoom ? { type: 'x' } : {},
        scrollablePlotArea: {
          minWidth: Math.max(cTimes.length * columnWidth, maxColumnsOnScreen * columnWidth),
          scrollPositionX: 100000000,
        },
      },
      scrollbar: { buttonsEnabled: true, enabled: true, showFull: true, zIndex: 1000 },
      title: { text: title },
      xAxis: {
        reversed: true,
        categories: cTimes,
        labels: {
          formatter() {
            return this.value ? dayjs(this.value, initFormat).format(format) : '';
          },
        },
        events: {
          afterSetExtremes: function (e) {
            if (e.max - e.min <= 6) {
              setFormat(constants.DATE_TIME_FORMAT);
            } else {
              setFormat(constants.DATE_FORMAT);
            }
          },
        },
      },
      yAxis: {
        opposite: true,
        title: {
          text: 'Giá trị',
        },
      },
      plotOptions: {
        series: {
          states: {
            select: {
              borderColor: 'pink',
              borderWidth: 2,
            },
            inactive: {
              opacity: 1,
            },
          },
        },
        column: {
          pointWidth: columnWidth,
          dataLabels: {
            enabled: true,
            format: '{y}',
          },
        },
      },
      series: cSeries,
    }),
    [cTimes, title, cSeries, format, zoom],
  );

  return (
    <div
      className={`[&_.highcharts-scrolling]:overflow-hidden ${!zoom ? '[&_.highcharts-scrolling]:cursor-move' : '[&_.highcharts-scrolling]:cursor-zoom-in'} ${isFetching ? 'pointer-events-none' : ''}`}
    >
      <div className="text-right mb-2">
        <Button className="font-bold" onClick={() => setZoom(!zoom)} type={zoom ? 'primary' : 'default'}>
          Zoom: {zoom ? 'bật' : 'tắt'}
        </Button>
      </div>
      {isFetching ? (
        chartRef.current?.container?.current?.outerHTML ? (
          <div dangerouslySetInnerHTML={{ __html: chartRef.current?.container?.current?.outerHTML }}></div>
        ) : (
          ''
        )
      ) : (
        <HighchartsReact ref={chartRef} className="w-full " highcharts={Highcharts} options={options} />
      )}
    </div>
  );
};

export default ViewChart;
